/**
 * サイドナビの定義。アイコンは 05-ui §11.5 の対応表に従う。
 *
 * `perm` を持つ項目は、その権限が無いユーザーには**出さない**。
 * 押しても 403 になるだけの項目を並べると、権限設計を読み違えたと勘違いされる。
 */
import {
  BookLock,
  Boxes,
  Building2,
  CreditCard,
  DatabaseBackup,
  EyeOff,
  FileSearch,
  Gauge,
  Globe,
  HandCoins,
  Inbox,
  KeyRound,
  Layers,
  LifeBuoy,
  ListChecks,
  Mail,
  Megaphone,
  PackageCheck,
  Radar,
  Receipt,
  Recycle,
  Send,
  Server,
  Settings2,
  ShieldCheck,
  Siren,
  Sparkles,
  Users,
} from 'lucide-react';
import type { NavGroup } from '@mail-sv/ui';
import { OPS_PERMISSION, type OpsPermission } from './permissions.js';

interface OpsNavItem {
  key: string;
  label: string;
  href: string;
  icon: NavGroup['items'][number]['icon'];
  perm?: OpsPermission;
}

interface OpsNavGroup {
  key: string;
  label: string;
  items: OpsNavItem[];
}

const NAV: OpsNavGroup[] = [
  {
    key: 'overview',
    label: '概況',
    items: [{ key: 'dashboard', label: 'ダッシュボード', href: '/', icon: Gauge }],
  },
  {
    key: 'infra',
    label: 'インフラ',
    items: [
      { key: 'nodes', label: 'サーバーノード', href: '/nodes', icon: Server, perm: OPS_PERMISSION.nodeRead },
      { key: 'nodePools', label: 'ノードプール', href: '/node-pools', icon: Boxes, perm: OPS_PERMISSION.nodeRead },
      { key: 'jobs', label: 'ジョブ', href: '/jobs', icon: ListChecks, perm: OPS_PERMISSION.nodeRead },
      {
        key: 'deliverability',
        label: '到達性',
        href: '/deliverability',
        icon: Radar,
        perm: OPS_PERMISSION.nodeRead,
      },
      {
        key: 'backup',
        label: 'バックアップ',
        href: '/backup',
        icon: DatabaseBackup,
        perm: OPS_PERMISSION.backupRead,
      },
      {
        key: 'certificates',
        label: '証明書・DKIM',
        href: '/certificates',
        icon: KeyRound,
        perm: OPS_PERMISSION.certRead,
      },
      { key: 'updates', label: 'アップデート配信', href: '/updates', icon: PackageCheck, perm: OPS_PERMISSION.updateRead },
      { key: 'security', label: 'セキュリティ', href: '/security', icon: ShieldCheck, perm: OPS_PERMISSION.nodeRead },
      /*
       * 画像プロキシの出口 IP と既知トラッカー。**`ops.security.read` で守る**
       * （API 側の 8 本がすべてこの権限。ナビだけ緩くすると 403 の画面へ誘導する）。
       */
      {
        key: 'privacy',
        label: 'プライバシー保護',
        href: '/privacy',
        icon: EyeOff,
        perm: OPS_PERMISSION.securityRead,
      },
      /*
       * 使い捨てアドレスの濫用監視。ops API 4 本がここに繋がる
       * （画面が無かったころは `abuse-scan` を叩く経路が人にも機械にも
       * 無く、起票が一度も走っていなかった）。権限は API と同じ
       * `ops.security.read`。
       */
      {
        key: 'privateAddresses',
        label: '使い捨てアドレス',
        href: '/private-addresses',
        icon: Recycle,
        perm: OPS_PERMISSION.securityRead,
      },
    ],
  },
  {
    key: 'tenants',
    label: '契約',
    items: [
      { key: 'organizations', label: '契約組織', href: '/organizations', icon: Building2, perm: OPS_PERMISSION.orgRead },
      { key: 'domains', label: 'ドメイン', href: '/domains', icon: Globe, perm: OPS_PERMISSION.orgRead },
      { key: 'mailboxes', label: 'メールボックス', href: '/mailboxes', icon: Mail, perm: OPS_PERMISSION.orgRead },
      { key: 'webmail', label: 'Webメール連携', href: '/webmail', icon: Inbox, perm: OPS_PERMISSION.webmailRead },
      /*
       * Webメールの AI。共有プロバイダの設定は `ops.settings.read`、
       * 組織ごとの状態は `ops.org.read` で守られている。**両方持たない者には
       * 画面自体が空になる**ので、ナビは緩いほう（org.read）で出す。
       */
      { key: 'ai', label: 'AI 設定', href: '/ai', icon: Sparkles, perm: OPS_PERMISSION.orgRead },
      // 配信の監視は `ops.bulk.halt`（＝止められる者）だけに出す。
      // 見えても止められない画面は、事故のときに「誰かが対応中」と誤解させる。
      { key: 'campaigns', label: '一斉配信', href: '/campaigns', icon: Send, perm: OPS_PERMISSION.bulkHalt },
      /*
       * 本文の break-glass 閲覧。**参照だけの権限が無い**（申請・承認・閲覧が
       * すべて `ops.content.read_body`）ので、持っていない運営者には出さない。
       */
      {
        key: 'breakGlass',
        label: '本文の閲覧統制',
        href: '/break-glass',
        icon: BookLock,
        perm: OPS_PERMISSION.contentReadBody,
      },
    ],
  },
  {
    key: 'billing',
    label: '料金',
    items: [
      { key: 'plans', label: 'プラン', href: '/plans', icon: Layers, perm: OPS_PERMISSION.planRead },
      { key: 'subscriptions', label: '契約プラン', href: '/subscriptions', icon: CreditCard, perm: OPS_PERMISSION.billingRead },
      { key: 'invoices', label: '請求・入金', href: '/invoices', icon: Receipt, perm: OPS_PERMISSION.billingRead },
      {
        key: 'slaCredits',
        label: 'SLA クレジット',
        href: '/sla-credits',
        icon: HandCoins,
        perm: OPS_PERMISSION.billingRead,
      },
    ],
  },
  {
    key: 'platform',
    label: 'プラットフォーム',
    items: [
      {
        key: 'support',
        label: '問い合わせ',
        href: '/support',
        icon: LifeBuoy,
        perm: OPS_PERMISSION.supportTicketRead,
      },
      { key: 'incidents', label: '障害インシデント', href: '/incidents', icon: Siren, perm: OPS_PERMISSION.securityRead },
      {
        key: 'announcements',
        label: '告知・メンテナンス',
        href: '/announcements',
        icon: Megaphone,
        perm: OPS_PERMISSION.securityRead,
      },
      { key: 'audit', label: '監査ログ', href: '/audit', icon: FileSearch, perm: OPS_PERMISSION.auditRead },
      { key: 'staff', label: '運営ユーザー', href: '/staff', icon: Users, perm: OPS_PERMISSION.staffRead },
      { key: 'settings', label: '設定', href: '/settings', icon: Settings2 },
    ],
  },
];

/** 権限で間引いたナビを返す。バッジ（未対応件数）は呼び出し側が差し込む。 */
export function buildNavGroups(
  perms: readonly string[] | undefined,
  badges: Record<string, { value: number; tone?: 'default' | 'warning' | 'destructive' }> = {},
): NavGroup[] {
  return NAV.map((group) => ({
    key: group.key,
    label: group.label,
    items: group.items
      .filter((item) => !item.perm || (perms?.includes(item.perm) ?? false))
      .map((item) => {
        const badge = badges[item.key];
        return {
          key: item.key,
          label: item.label,
          href: item.href,
          icon: item.icon,
          ...(badge && badge.value > 0
            ? { badge: badge.value, badgeTone: badge.tone ?? 'default' }
            : {}),
        };
      }),
  })).filter((group) => group.items.length > 0);
}
